import api from '../../services/api'

/**
 * API client for Analysis endpoints
 * Full API documentation: docs/FRONTEND_ANALYSIS_FEATURE_GUIDE.md
 */
export const analysesApi = {
  /**
   * List analyses (paginated)
   * @param {Object} params - Query parameters
   * @param {boolean} params.include_deleted - Include soft-deleted analyses
   * @returns {Promise} Response with { count, next, previous, results }
   */
  list: (params = {}) => api.get('/analyses/', { params }),

  /**
   * Get dashboard data grouped by status
   * @returns {Promise} { needs_attention, in_progress, completed, failed }
   */
  getDashboard: () => api.get('/analyses/dashboard/'),

  /**
   * Get analysis detail with steps and files
   * @param {string} id - Analysis UUID
   * @returns {Promise} Full analysis object with steps[] and analysis_files[]
   */
  get: (id) => api.get(`/analyses/${id}/`),

  /**
   * Create new analysis
   * @param {Object} data
   * @param {string} data.project - Project UUID
   * @param {string} data.analysis_type - Currently only 'synchro_parser'
   * @param {string} data.name - User-provided name
   * @param {string[]} data.input_file_ids - Array of file UUIDs (must be completed files)
   * @returns {Promise} Created analysis object
   */
  create: (data) => api.post('/analyses/', data),

  /**
   * Start analysis (moves from configuring to processing)
   * @param {string} id - Analysis UUID
   * @returns {Promise} { message, analysis_id, current_step, status }
   */
  start: (id) => api.post(`/analyses/${id}/start/`),

  /**
   * Get current step status (for polling)
   * @param {string} id - Analysis UUID
   * @returns {Promise} { analysis_status, current_step, step: {...} }
   */
  getCurrentStep: (id) => api.get(`/analyses/${id}/current-step/`),

  /**
   * Submit user input for configuration step
   * @param {string} id - Analysis UUID
   * @param {Object} inputData - Step-specific input data
   * @returns {Promise} { message, analysis_status, current_step }
   */
  submitStep: (id, inputData) =>
    api.post(`/analyses/${id}/submit-step/`, { input_data: inputData }),

  /**
   * List all steps for analysis
   * @param {string} id - Analysis UUID
   * @returns {Promise} Array of step objects (not paginated)
   */
  listSteps: (id) => api.get(`/analyses/${id}/steps/`),

  /**
   * Get specific step by number
   * @param {string} id - Analysis UUID
   * @param {number} stepNumber - Step number (1-5 for Synchro)
   * @returns {Promise} Step object
   */
  getStep: (id, stepNumber) => api.get(`/analyses/${id}/steps/${stepNumber}/`),

  /**
   * Cancel running analysis
   * @param {string} id - Analysis UUID
   * @returns {Promise} { message, analysis_id, status: 'failed' }
   */
  cancel: (id) => api.post(`/analyses/${id}/cancel/`),

  /**
   * Soft delete analysis (30-day retention)
   * @param {string} id - Analysis UUID
   * @returns {Promise} 204 No Content
   */
  delete: (id) => api.delete(`/analyses/${id}/`),

  /**
   * Restore soft-deleted analysis
   * @param {string} id - Analysis UUID
   * @returns {Promise} { message, analysis: {...} }
   */
  restore: (id) => api.post(`/analyses/${id}/restore/`),

  /**
   * Adjust input (volume figure generator only)
   * Resets from step 3 (preview) back to step 1 (input form)
   * @param {string} id - Analysis UUID
   * @returns {Promise} { message, analysis: {...} }
   */
  adjustInput: (id) => api.post(`/analyses/${id}/adjust-input/`),

  /**
   * Download file (output files from analysis)
   * @param {string} fileId - File UUID
   * @returns {Promise} Blob response with file data
   */
  downloadFile: (fileId) =>
    api.get(`/files/${fileId}/download/`, {
      responseType: 'blob',
    }),

  /**
   * Fetch Synchro Parser changelog
   * @returns {Promise} { content: string, current_version: string }
   */
  getSynchroChangelog: () => api.get('/analyses/synchro-parser/changelog/'),
}
